import React, { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Navbar, Footer } from "../components/Layout";
import { Trophy, Clock, Check, X, Crown, ArrowRight, RotateCcw } from "lucide-react";
import { useSEO } from "../hooks/useSEO";

const LEVELS = ["N5", "N4", "N3"];

const JLPTMock = () => {
  const { user, loading: authLoading } = useAuth();
  const [step, setStep] = useState("select"); // select | exam | result
  const [level, setLevel] = useState("N5");
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const submittedRef = React.useRef(false);

  useSEO({
    title: "Examens blancs JLPT N5, N4, N3 : Test gratuit | Japonais Pas à Pas",
    description: "Préparez le JLPT avec nos examens blancs minutés N5, N4 et N3. Vocabulaire, grammaire, kanji, compréhension. Score immédiat et révision des erreurs.",
    canonical: "https://japonais-pas-a-pas.com/jlpt",
  });

  // Timer
  useEffect(() => {
    if (step !== "exam") return;
    const id = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [step]);

  // Auto-submit on expiry
  useEffect(() => {
    if (step === "exam" && secondsLeft === 0 && questions.length > 0 && !submittedRef.current) {
      submittedRef.current = true;
      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [secondsLeft, step]);

  if (authLoading) return <div className="washi-bg min-h-screen"><Navbar /></div>;

  const start = async (lvl, count) => {
    try {
      const { data } = await api.get(`/jlpt/${lvl}/questions?count=${count}`);
      if (!data.questions?.length) {
        return alert("Banque de questions vide pour ce niveau.");
      }
      setLevel(lvl);
      setQuestions(data.questions);
      setAnswers({});
      setResult(null);
      setSecondsLeft(count * 60); // 1 min per question
      submittedRef.current = false;
      setStep("exam");
    } catch (err) {
      if (err.response?.status === 402) {
        alert("Le mode examen N4 et N3 est réservé aux abonnés Premium.");
      } else alert("Erreur");
    }
  };

  const submit = async () => {
    try {
      const { data } = await api.post("/jlpt/submit", { level, answers });
      setResult(data);
      setStep("result");
    } catch {
      alert("Erreur soumission");
    }
  };

  const mmss = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

  return (
    <div className="washi-bg min-h-screen" data-testid="jlpt-page">
      <Navbar />
      <div className="max-w-4xl mx-auto px-6 sm:px-12 py-12">

        {step === "select" && (
          <>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#E94A3F] mb-3">JLPT Blanc</div>
            <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tighter leading-[1] text-[#1A2A40] mb-3">
              Examen blanc.<br /><span className="italic font-light">Conditions réelles.</span>
            </h1>
            <p className="text-lg text-[#4A5568] max-w-2xl mb-10">
              Vocabulaire, grammaire, kanji, compréhension écrite. Limite de temps. Score immédiat avec correction détaillée.
            </p>
            <div className="grid md:grid-cols-3 gap-5">
              {LEVELS.map((lvl) => {
                const isLocked = lvl !== "N5" && !user?.is_premium;
                return (
                  <button
                    key={lvl}
                    onClick={() => user ? start(lvl, lvl === "N5" && !user.is_premium ? 10 : 30) : alert("Connectez-vous d'abord")}
                    data-testid={`start-${lvl}`}
                    className={`relative rounded-3xl p-7 border-2 hover-lift text-left ${
                      isLocked ? "bg-[#F3F6F1] border-[#1A2A40]/10" : "bg-[#FDFBF7] border-[#1A2A40]/15"
                    }`}
                  >
                    {isLocked && (
                      <div className="absolute top-4 right-4 bg-[#1A2A40] text-[#FFD166] rounded-full px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1">
                        <Crown className="w-3 h-3" /> Premium
                      </div>
                    )}
                    <div className="font-display font-black text-5xl text-[#E94A3F] mb-3">{lvl}</div>
                    <div className="text-sm text-[#4A5568] mb-3">
                      {lvl === "N5" ? "Débutant · Bases" : lvl === "N4" ? "Faux débutant" : "Intermédiaire"}
                    </div>
                    <div className="text-xs uppercase tracking-widest text-[#A0AEC0] mb-1">
                      {user?.is_premium ? "30 questions · 30 min" : lvl === "N5" ? "10 questions · 10 min (gratuit)" : "30 questions · 30 min"}
                    </div>
                  </button>
                );
              })}
            </div>
          </>
        )}

        {step === "exam" && questions.length > 0 && (
          <>
            <div className="flex justify-between items-center mb-6 sticky top-20 bg-[#FDFBF7]/90 backdrop-blur z-10 py-3 rounded-xl border border-[#1A2A40]/10 px-4" data-testid="exam-header">
              <div className="font-display font-black text-2xl text-[#1A2A40]">JLPT {level}</div>
              <div className="flex items-center gap-3">
                <div className="text-sm text-[#4A5568]">
                  {Object.keys(answers).length}/{questions.length}
                </div>
                <div className={`flex items-center gap-2 font-mono font-bold ${secondsLeft < 60 ? "text-[#E94A3F]" : "text-[#1A2A40]"}`} data-testid="exam-timer">
                  <Clock className="w-4 h-4" /> {mmss(secondsLeft)}
                </div>
              </div>
            </div>

            <div className="space-y-5">
              {questions.map((q, i) => (
                <div key={q.id} className="bg-[#FDFBF7] rounded-2xl p-6 border-2 border-[#1A2A40]/10" data-testid={`q-${q.id}`}>
                  <div className="text-[10px] uppercase tracking-widest text-[#E94A3F] font-bold mb-1">Q{i + 1} · {q.category}</div>
                  <div className="text-lg font-bold text-[#1A2A40] mb-4 leading-snug">{q.question}</div>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {q.options.map((opt, oi) => (
                      <button
                        key={oi}
                        onClick={() => setAnswers({ ...answers, [q.id]: oi })}
                        data-testid={`q-${q.id}-opt-${oi}`}
                        className={`text-left rounded-xl px-4 py-3 border-2 transition-all font-medium text-sm ${
                          answers[q.id] === oi
                            ? "bg-[#E94A3F] text-white border-[#E94A3F]"
                            : "bg-white text-[#1A2A40] border-[#1A2A40]/15 hover:border-[#E94A3F]"
                        }`}
                      >
                        <span className="font-mono text-xs mr-2">{"ABCD"[oi]}</span>{opt}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={submit}
              data-testid="submit-exam-btn"
              className="mt-8 w-full bg-[#E94A3F] text-white font-bold py-4 rounded-xl shadow-brut hover-lift inline-flex items-center justify-center gap-2"
            >
              Soumettre l'examen <ArrowRight className="w-4 h-4" />
            </button>
          </>
        )}

        {step === "result" && result && (
          <div data-testid="result-page">
            <div className={`rounded-3xl p-10 border-2 shadow-brut mb-8 ${result.passed ? "bg-[#8CA07A] text-white border-[#8CA07A]" : "bg-[#1A2A40] text-white border-[#1A2A40]"}`}>
              <Trophy className="w-12 h-12 mb-4" />
              <div className="text-xs font-bold uppercase tracking-widest text-[#FFD166] mb-2">
                {result.passed ? "Réussi ! 合格" : "Pas encore... 頑張って"}
              </div>
              <div className="font-display font-black text-7xl mb-2">{result.pct}%</div>
              <div className="text-2xl font-display font-bold mb-1">{result.score} / {result.total} bonnes réponses</div>
              <div className="text-sm opacity-80">JLPT {result.level} · Seuil de réussite : 60%</div>
            </div>

            <h2 className="font-display font-black text-2xl mb-4 text-[#1A2A40]">Détail par question</h2>
            <div className="space-y-3">
              {result.breakdown.map((b, i) => (
                <div key={b.id} className={`rounded-2xl p-5 border-2 ${b.is_correct ? "bg-[#8CA07A]/10 border-[#8CA07A]/30" : "bg-[#E94A3F]/10 border-[#E94A3F]/30"}`}>
                  <div className="flex items-start gap-3 mb-2">
                    {b.is_correct ? <Check className="w-5 h-5 text-[#8CA07A] mt-0.5" /> : <X className="w-5 h-5 text-[#E94A3F] mt-0.5" />}
                    <div className="text-sm">
                      <div className="font-bold text-[#1A2A40]">Q{i + 1} · {b.category}</div>
                      <div className="text-xs text-[#4A5568] mt-1">Votre réponse : <strong>{"ABCD"[b.your_answer] || "—"}</strong> · Bonne : <strong>{"ABCD"[b.correct_answer]}</strong></div>
                    </div>
                  </div>
                  {b.explanation && <p className="text-sm text-[#4A5568] pl-8">{b.explanation}</p>}
                </div>
              ))}
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <button onClick={() => setStep("select")} data-testid="retry-btn" className="flex-1 bg-[#1A2A40] text-white font-bold py-3 rounded-xl inline-flex items-center justify-center gap-2">
                <RotateCcw className="w-4 h-4" /> Refaire un examen
              </button>
              <Link to="/review" className="flex-1 bg-[#E94A3F] text-white font-bold py-3 rounded-xl text-center shadow-brut-sm hover-lift">
                Réviser mes points faibles →
              </Link>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default JLPTMock;
